import Immutable from 'immutable';
import unescapedText from 'I18n/utils/unescapedText';
import Routes from 'ContentUtils/Routes';
import DesignAssetTypes from 'ContentUtils/constants/DesignAssetTypes';
import { createSelector } from 'reselect';
import { getThemePath, getThemePreviewAbsolutePath } from './themeSelectors';
import { getSelectedPreviewAssetValue, getPreviewTemplatePathFromProps } from './appSelectors';
import { getHasDesignManagerAccessScope } from './authSelectors';
import { DRAFT_STATES, PUBLISHED_STATES, StateGroups } from '../constants/States';
import AllTemplateTypes from 'ContentUtils/constants/TemplateTypes';
const EMPTY_LIST = Immutable.List();
const EMAIL_TYPES = Object.freeze({
  [AllTemplateTypes.byName.email.id]: 2,
  [AllTemplateTypes.byName.drag_drop_email.id]: 34,
  [AllTemplateTypes.byName.membership_email.id]: 36
});
const CUSTOMER_PORTAL_THEME_PATH = '@hubspot/customer-portal';
export const SYSTEM_STATES_IDS = Object.freeze({
  11: 11,
  12: 12,
  19: 19,
  14: 14,
  13: 13,
  27: 27
});
export const KNOWLEDGE_BASE_TEMPLATE_TYPES = Object.freeze({
  35: 35,
  48: 48,
  49: 49,
  50: 50,
  51: 51,
  52: 52
});
const SECTIONS_TEMPLATE_TYPE = [37, 46];
const stateI18nKey = state => `theme-previewer.states.${state}`;
const getDependentTemplatesState = state => state.themeDependentAssets.templates;
const getDependentPagesState = state => state.themeDependentAssets.pages;
const getContentPreviewKeysState = state => state.themeDependentAssets.contentPreviewKeys;
export const getTemplatePreviewKeys = state => state.themeDependentAssets.templatePreviewKeys;
const getPreviewPageId = (state, props) => props.previewPageId;
export const getDependentPagesRequestStatus = createSelector([getDependentPagesState], dependentPagesState => dependentPagesState.requestStatus);
export const getDependentTemplatesRequestStatus = createSelector([getDependentTemplatesState], dependentTemplatesState => dependentTemplatesState.requestStatus);
export const getContentPreviewKeyRequestStatus = createSelector([getContentPreviewKeysState], previewKeysState => previewKeysState.requestStatus);
export const getSortedDependantTemplatesAsList = createSelector([getThemePreviewAbsolutePath, getDependentTemplatesState], (themePreviewAbsolutePath, dependantTemplateState) => {
  return dependantTemplateState.objects.reduce((final, template) => {
    const path = template.get('path');
    const link = Routes.designManager(template.get('id'), DesignAssetTypes.TEMPLATE, template.get('assetSource'), path);
    return final.push({
      path,
      link,
      label: template.get('displayName') || template.get('label'),
      templateType: template.get('templateType'),
      isAvailableForNewContent: template.get('isAvailableForNewContent')
    });
  }, EMPTY_LIST).sort((a, b) => {
    const aLabel = a.label.toLowerCase();
    const bLabel = b.label.toLowerCase();
    // Always show the theme preview template at the top of the list
    if (a.path === themePreviewAbsolutePath) return -1;
    if (b.path === themePreviewAbsolutePath) return 1;
    if (aLabel < bLabel) return -1;
    if (aLabel > bLabel) return 1;
    return 0;
  });
});
const getSortedDependantPagesAsList = createSelector([getDependentPagesState], dependantPagesState => {
  return dependantPagesState.objects.reduce((final, page) => {
    const id = page.get('id');
    const categoryId = page.get('categoryId');
    const state = page.get('state');
    const language = page.get('language');
    return final.push({
      label: page.get('displayName') || page.get('label'),
      language,
      link: Routes.inpageEdit(id, categoryId),
      state,
      id
    });
  }, EMPTY_LIST).sort((a, b) => {
    const aLabel = a.label.toLowerCase();
    const bLabel = b.label.toLowerCase();
    if (aLabel < bLabel) return -1;
    if (aLabel > bLabel) return 1;
    return 0;
  });
});
const getAssetDataAsOption = assetData => {
  return {
    text: assetData.label,
    value: assetData.path || assetData.id,
    type: assetData.templateType || null,
    language: assetData.language,
    link: assetData.link,
    state: assetData.state
  };
};
export const getDependentTemplateOptions = createSelector([getThemePreviewAbsolutePath, getSortedDependantTemplatesAsList], (themePreviewAbsolutePath, sortedDependantTemplatesAsList) => {
  return sortedDependantTemplatesAsList.filter(templateData => {
    return templateData.isAvailableForNewContent || templateData.path === themePreviewAbsolutePath;
  }).map(getAssetDataAsOption);
});
export const getDependentPageOptions = createSelector([getSortedDependantPagesAsList], sortedDependantPagesAsList => {
  return sortedDependantPagesAsList.map(getAssetDataAsOption);
});
const getPublishedPageGroupedOptions = createSelector([getDependentPageOptions], dependentPageOptions => {
  const {
    PUBLISHED_PAGES
  } = StateGroups;
  const options = dependentPageOptions.reduce((acc, pageOption) => {
    const {
      state
    } = pageOption;
    if (PUBLISHED_STATES.includes(state)) {
      acc.push(pageOption);
    }
    return acc;
  }, []);
  return {
    disableLinks: false,
    disabledMessage: undefined,
    state: PUBLISHED_PAGES,
    options,
    text: unescapedText(stateI18nKey(PUBLISHED_PAGES), {
      count: options.length
    })
  };
});
const getSystemTemplateGroupedOptions = createSelector([getDependentTemplateOptions], dependentTemplateOptions => {
  const {
    SYSTEM
  } = StateGroups;
  const options = dependentTemplateOptions.toJS().filter(template => {
    return SYSTEM_STATES_IDS[template.type];
  });
  return {
    disableLinks: false,
    disabledMessage: undefined,
    state: SYSTEM,
    options,
    text: unescapedText(stateI18nKey(SYSTEM), {
      count: options.length
    })
  };
});
const getKbTemplateGroupedOptions = createSelector([getDependentTemplateOptions], dependentTemplateOptions => {
  const {
    KNOWLEDGE_BASE
  } = StateGroups;
  const options = dependentTemplateOptions.toJS().filter(template => {
    return KNOWLEDGE_BASE_TEMPLATE_TYPES[template.type];
  });
  return {
    disableLinks: false,
    disabledMessage: undefined,
    options,
    state: KNOWLEDGE_BASE,
    text: unescapedText(stateI18nKey(KNOWLEDGE_BASE), {
      count: options.length
    })
  };
});
const getDraftPageGroupedOptions = createSelector([getDependentPageOptions], dependentPageOptions => {
  const {
    DRAFT_PAGES
  } = StateGroups;
  const options = dependentPageOptions.reduce((acc, pageOption) => {
    const {
      state
    } = pageOption;
    if (DRAFT_STATES.includes(state)) {
      acc.push(pageOption);
    }
    return acc;
  }, []);
  return {
    disableLinks: false,
    disabledMessage: undefined,
    state: DRAFT_PAGES,
    options,
    text: unescapedText(stateI18nKey(DRAFT_PAGES), {
      count: options.length
    })
  };
});
const getTemplateGroupedOptions = createSelector([getDependentTemplateOptions, getHasDesignManagerAccessScope, getThemePath], (dependentTemplateOptions, hasDesignManagerAccess, themePath) => {
  const {
    TEMPLATES
  } = StateGroups;
  const options = dependentTemplateOptions.toJS().filter(template => {
    return !SYSTEM_STATES_IDS[template.type] && !KNOWLEDGE_BASE_TEMPLATE_TYPES[template.type] && !SECTIONS_TEMPLATE_TYPE.includes(template.type);
  });
  return {
    state: TEMPLATES,
    disableLinks: !hasDesignManagerAccess || themePath === CUSTOMER_PORTAL_THEME_PATH,
    disabledMessage: !hasDesignManagerAccess ? unescapedText(stateI18nKey(`disabledMessage.${TEMPLATES}`)) : unescapedText(stateI18nKey(`disabledMessage.CUSTOMER_PORTAL`)),
    options,
    text: unescapedText(stateI18nKey(TEMPLATES), {
      count: options.length
    })
  };
});
export const getGroupedDependentAssetOptions = createSelector([getPublishedPageGroupedOptions, getDraftPageGroupedOptions, getTemplateGroupedOptions, getKbTemplateGroupedOptions, getSystemTemplateGroupedOptions], (publishedPageGroupedOptions, draftPageGroupedOptions, templateGroupedOptions, kbTemplateGroupedOptions, systemTemplateGroupedOptions) => {
  return [publishedPageGroupedOptions, draftPageGroupedOptions, templateGroupedOptions, kbTemplateGroupedOptions, systemTemplateGroupedOptions];
});
export const getGroupedDependentAssetOptionsCount = createSelector(getGroupedDependentAssetOptions, groupedDependentAssetOptions => groupedDependentAssetOptions.reduce((count, group) => count + group.options.length, 0));
export const getTotalTemplateConnections = createSelector([getDependentTemplateOptions], templateOptions => templateOptions.size);
export const getContentPreviewKeys = createSelector([getContentPreviewKeysState], previewKeysState => previewKeysState.contentPreviewKeys);
export const getContentPreviewKey = createSelector([getContentPreviewKeys, getPreviewPageId], (previewKeys, previewPageId) => previewKeys[previewPageId]);
export const getTemplatePreviewKey = createSelector([getTemplatePreviewKeys, getPreviewTemplatePathFromProps], (templatePreviewKeys, selectedTemplatePath) => {
  const {
    earlyRequestSelectedTemplate,
    earlyRequestPreviewKey
  } = window;
  return selectedTemplatePath && templatePreviewKeys[selectedTemplatePath] || (selectedTemplatePath === earlyRequestSelectedTemplate ? earlyRequestPreviewKey : undefined);
});
const getSortedDependentsAsList = createSelector([getSortedDependantPagesAsList, getSortedDependantTemplatesAsList], (sortedDependantPagesAsList, sortedDependantTemplatesAsList) => sortedDependantPagesAsList.concat(sortedDependantTemplatesAsList));
export const getSelectedDependentIndex = createSelector([getSortedDependentsAsList, getSelectedPreviewAssetValue], (sortedDependentsList, selectedPreviewAssetValue) => {
  const assetIdOrPathList = sortedDependentsList.map(dependent => dependent.id || dependent.path);
  return selectedPreviewAssetValue ? assetIdOrPathList.indexOf(selectedPreviewAssetValue) : -1;
});
export const getIsSelectedDependentEmail = createSelector([getSortedDependentsAsList, getSelectedDependentIndex], (sortedDependentsList, selectedAssetIndex) => {
  const dependent = sortedDependentsList.get(selectedAssetIndex);
  return !!(dependent && EMAIL_TYPES[dependent.templateType]);
});